import * as React from "react";
import { cn } from "../lib/utils";

export interface AsrtLogoProps extends React.SVGAttributes<SVGSVGElement> {
  /** Rendered height in px (width auto-scales). Default 24. */
  size?: number;
  /** Show the "ASRT SYSTEMS" wordmark next to the monogram. Default true. */
  showWordmark?: boolean;
  /** Accessible title. Default "ASRT Systems". */
  title?: string;
}

/**
 * ASRT Systems wordmark — a square monogram built from two chevrons forming an
 * "A", plus optional display-font wordmark. Pure inline SVG, crisp at 16/20/24/32px.
 */
const AsrtLogo = React.forwardRef<SVGSVGElement, AsrtLogoProps>(
  ({ className, size = 24, showWordmark = true, title = "ASRT Systems", ...props }, ref) => {
    const width = showWordmark ? size * 5.4 : size;
    return (
      <svg
        ref={ref}
        role="img"
        aria-label={title}
        width={width}
        height={size}
        viewBox={`0 0 ${showWordmark ? 162 : 30} 30`}
        fill="none"
        className={cn("shrink-0", className)}
        focusable="false"
        {...props}
      >
        <title>{title}</title>
        {/* Monogram: rounded square + two chevrons forming an "A". */}
        <rect x="0.75" y="0.75" width="28.5" height="28.5" rx="7" className="fill-primary" />
        <path
          d="M15 7.5 L21.5 22 M15 7.5 L8.5 22"
          stroke="hsl(var(--asrt-primary-foreground))"
          strokeWidth="2.6"
          strokeLinecap="round"
        />
        <path d="M11 17.5 H19" stroke="hsl(var(--asrt-primary-foreground))" strokeWidth="2.6" strokeLinecap="round" />
        {showWordmark ? (
          <>
            <text
              x="37"
              y="14"
              className="font-display"
              fill="currentColor"
              fontSize="11.5"
              fontWeight="700"
              letterSpacing="0.14em"
            >
              ASRT
            </text>
            <text
              x="37"
              y="25"
              className="font-display"
              fill="currentColor"
              fillOpacity="0.62"
              fontSize="8"
              fontWeight="600"
              letterSpacing="0.26em"
            >
              SYSTEMS
            </text>
          </>
        ) : null}
      </svg>
    );
  },
);
AsrtLogo.displayName = "AsrtLogo";

export { AsrtLogo };
